import { API_BASE_URL } from '@/api/config';
import { ApiError } from '@/auth/api';

/// A reservation as its buyer sees it (VEN-3): no branch and nothing internal (P-I5).
export type BuyerReservation = {
  id: string;
  /// The six characters people read and dictate (`A3F9K2`), the same the panel shows.
  reference: string;
  /// EXPIRED also for an active one whose end passed, before VEN-5 releases it.
  status: 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'COMPLETED';
  device: { brand: string | null; model: string; storageGb: number | null; color: string | null };
  priceMinor: number;
  expiresAt: string;
  createdAt: string;
};

/** Keeps `details` (the `code` of a refusal) so the page can say what to do. */
async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    const { error, ...details } = data;
    throw new ApiError(
      response.status,
      typeof error === 'string' && error ? error : 'Request failed',
      details,
    );
  }

  return data as T;
}

const authHeaders = (token: string) => ({ Authorization: `Bearer ${token}` });

/** Reserve a published device for three days, without payment (VEN-3). */
export async function reserveDeviceRequest(
  token: string,
  deviceId: string,
): Promise<BuyerReservation> {
  const response = await fetch(`${API_BASE_URL}/api/catalog/devices/${deviceId}/reservations`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return (await parseJsonResponse<{ reservation: BuyerReservation }>(response)).reservation;
}

/// An online purchase as its buyer sees it (VEN-7), always as the backend recorded it (BR-045).
export type BuyerPurchase = {
  id: string;
  reference: string;
  status: 'CONFIRMED' | 'PROCESSING' | 'REJECTED' | 'REFUND_PENDING' | 'REFUNDED' | 'NOT_COMPLETED';
  deviceId: string;
  device: BuyerReservation['device'];
  priceMinor: number;
  expiresAt: string;
  createdAt: string;
  soldAt: string | null;
  /// The device is still held for this purchase: the buyer can pay, or pay again.
  held: boolean;
  /// Held, and no payment is in review: the buyer can go back to Mercado Pago to pay.
  canResumePayment: boolean;
};

export type StartedPurchase = { purchaseId: string; checkoutUrl: string; expiresAt: string };

/** Start paying a published device in full; answers where to pay in Mercado Pago (VEN-7). */
export async function buyDeviceRequest(token: string, deviceId: string): Promise<StartedPurchase> {
  const response = await fetch(`${API_BASE_URL}/api/catalog/devices/${deviceId}/purchases`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return (await parseJsonResponse<{ purchase: StartedPurchase }>(response)).purchase;
}

export async function getMyPurchases(token: string): Promise<BuyerPurchase[]> {
  const response = await fetch(`${API_BASE_URL}/api/account/purchases`, {
    headers: authHeaders(token),
  });
  return (await parseJsonResponse<{ purchases: BuyerPurchase[] }>(response)).purchases;
}

export async function getMyPurchase(token: string, purchaseId: string): Promise<BuyerPurchase> {
  const response = await fetch(`${API_BASE_URL}/api/account/purchases/${purchaseId}`, {
    headers: authHeaders(token),
  });
  return (await parseJsonResponse<{ purchase: BuyerPurchase }>(response)).purchase;
}

export async function getMyReservations(token: string): Promise<BuyerReservation[]> {
  const response = await fetch(`${API_BASE_URL}/api/account/reservations`, {
    headers: authHeaders(token),
  });
  return (await parseJsonResponse<{ reservations: BuyerReservation[] }>(response)).reservations;
}

export async function getMyReservation(
  token: string,
  reservationId: string,
): Promise<BuyerReservation> {
  const response = await fetch(`${API_BASE_URL}/api/account/reservations/${reservationId}`, {
    headers: authHeaders(token),
  });
  return (await parseJsonResponse<{ reservation: BuyerReservation }>(response)).reservation;
}
